import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { 
  LayoutDashboard, 
  MessageSquare, 
  Users, 
  Tags, 
  Gift, 
  Settings,
  ChevronLeft,
  ChevronRight,
  Shield,
  LogOut,
  Store,
  Flag,
  Ticket,
  Home,
  ArrowRightLeft,
  ShieldX,
  Loader2,
  RefreshCw,
  SlidersHorizontal,
  MessageCircle,
  Globe,
  Stethoscope,
  AlertCircle,
  Menu,
  X,
  Network,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '@/hooks/usePermissions';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/restaurants', label: 'Restaurants', icon: Store },
  { path: '/admin/reviews', label: 'Reviews', icon: MessageSquare },
  { path: '/admin/comments', label: 'Comments', icon: MessageSquare },
  { path: '/admin/reports', label: 'Reports', icon: Flag },
  { path: '/admin/feedback', label: 'Feedback', icon: MessageCircle },
  { path: '/admin/users', label: 'Users', icon: Users },
  { path: '/admin/tags', label: 'Tags & Categories', icon: Tags },
  { path: '/admin/coupons', label: 'Coupons', icon: Ticket },
  { path: '/admin/gamification', label: 'Gamification', icon: Gift },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
  { path: '/admin/stripe-connect', label: 'Stripe Connect', icon: ArrowRightLeft },
  { path: '/admin/config', label: 'Config', icon: SlidersHorizontal },
  { path: '/admin/trusted-domains', label: 'Trusted Domains', icon: Globe },
  { path: '/admin/kudoz-probe', label: 'Kudoz Probe', icon: Stethoscope },
  { path: '/admin/error-log', label: 'Error Log', icon: AlertCircle },
  { path: '/admin/referral-network', label: 'Referral Network', icon: Network },
];

function SidebarContent({
  collapsed,
  location,
  onLinkClick,
}: {
  collapsed: boolean;
  location: string;
  onLinkClick?: () => void;
}) {
  return (
    <>
      <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location === item.path ||
            (item.path !== '/admin' && location.startsWith(item.path));

          return (
            <Link key={item.path} href={item.path} onClick={onLinkClick}>
              <div
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "hover-elevate text-muted-foreground hover:text-foreground"
                )}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
              </div>
            </Link>
          );
        })}
      </nav>

      <div className="p-2 border-t space-y-1">
        {!collapsed && (
          <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Switch Portal
          </div>
        )}
        <Link href="/" onClick={onLinkClick}>
          <div className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer hover-elevate text-muted-foreground hover:text-foreground"
            data-testid="nav-switch-user-app"
          >
            <Home className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">User App</span>}
          </div>
        </Link>
        <Link href="/partner" onClick={onLinkClick}>
          <div className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer hover-elevate text-muted-foreground hover:text-foreground"
            data-testid="nav-switch-partner"
          >
            <Store className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Partner Portal</span>}
          </div>
        </Link>
      </div>
    </>
  );
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('admin_sidebar_collapsed') === 'true'; }
    catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [location, navigate] = useLocation();

  const { data: roleData, isLoading: roleLoading } = useQuery<{ role: string; needsPermissionsCheck?: boolean }>({
    queryKey: ['/api/user/role'],
    retry: 1,
  });

  const { isAdminUser, isPending: permPending, isError: permError, error: permErrorDetail, refetch: refetchPerms } = usePermissions();

  const localIsAdmin = roleData?.role === 'admin' || roleData?.role === 'super_admin';
  const eatzyIsAdmin = isAdminUser();
  const isAdmin = localIsAdmin || eatzyIsAdmin;

  const stillLoading = roleLoading || (roleData?.needsPermissionsCheck && permPending);

  if (stillLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 text-center max-w-md px-6">
          <div className="rounded-full bg-destructive/10 p-4">
            <ShieldX className="h-10 w-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold">Access Denied</h1>
          <p className="text-muted-foreground">
            {permError
              ? (permErrorDetail?.message || "We couldn't verify your access. This may be a temporary issue.")
              : 'You don\'t have permission to access the Admin Portal. This area is restricted to administrators only.'}
          </p>
          {permError && (
            <Button variant="outline" onClick={() => refetchPerms()} data-testid="button-retry-permissions">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          )}
          <Button onClick={() => navigate('/')} data-testid="button-go-home">
            <Home className="h-4 w-4 mr-2" />
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 flex flex-col w-64 border-r bg-card transition-transform duration-300 md:hidden",
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex items-center justify-between h-14 px-4 border-b">
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary flex-shrink-0" />
            <span className="font-semibold text-lg">Admin Portal</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(false)}
            className="shrink-0"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        <SidebarContent
          collapsed={false}
          location={location}
          onLinkClick={() => setMobileOpen(false)}
        />
      </aside>

      {/* Desktop sidebar */}
      <aside className={cn(
        "hidden md:flex flex-col border-r bg-card transition-all duration-300 shrink-0",
        collapsed ? "w-16" : "w-64"
      )}>
        <div className="flex items-center gap-2 h-14 px-4 border-b">
          <Shield className="w-6 h-6 text-primary flex-shrink-0" />
          {!collapsed && (
            <span className="font-semibold text-lg">Admin Portal</span>
          )}
        </div>

        <SidebarContent collapsed={collapsed} location={location} />

        <div className="px-2 pb-2 border-t pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const next = !collapsed;
              setCollapsed(next);
              try { localStorage.setItem('admin_sidebar_collapsed', String(next)); } catch {}
            }}
            className="w-full justify-center"
            data-testid="button-toggle-sidebar"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="flex items-center gap-3 h-14 px-4 border-b bg-card md:hidden shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            data-testid="button-mobile-menu"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-semibold">Admin Portal</span>
          </div>
        </div>

        <div className="p-4 md:p-6 flex-1">
          {children}
        </div>
      </main>
    </div>
  );
}
